import { faCalendarPlus, faPlus } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { Banner, classnames, SingleFilterDropDown, SlideToggle, Toast, Modal, ModalBody, ModalFooter, ModalHeader } from '@ygtechnologies/bygr-frontend-component-library';
import { Button } from '@/components/Buttons';
import { ButtonColor } from '@/components/Buttons/ButtonElement';
import { DateDisplay } from '@/components/localization';
import React, { useEffect, useState } from 'react';
import { MeetingType, sendReminder, updateLetterSetupData } from '@/services/booking';
import { TranslatableText } from '@/types';
import { numberToClock } from '@/utils/date';
import { triggerHandler } from '@ygtechnologies/rubus-frontend-utilities';
import { LetterSetupData } from '../../../MeetingOverview';
import { ResidentialUnitMeetingData } from '../../MeetingApartments/MeetingApartments';
import theme from './ReminderDialog.module.scss';

interface ReminderDialogProps {
    selectedMeetings: ResidentialUnitMeetingData[];
    letterSetupData: LetterSetupData;
    activeTab: MeetingType;
    show: boolean;
    onSend(): void;
    onUpdate(): void;
    onClose(): void;
    onFilterChange(row: ResidentialUnitMeetingData, unitId: boolean): void;
    onCancel?(): void;
    confirmButtonColor?: ButtonColor;
    cancelButtonColor?: ButtonColor;
    confirmButtonText?: TranslatableText;
    cancelButtonText?: TranslatableText;
    /**
     * This is the "x" on top right
     */
    onCloseClick?(): void;
}

const ReminderDialog: React.FC<ReminderDialogProps> = ({
    selectedMeetings,
    letterSetupData,
    activeTab,
    show,
    onSend,
    onUpdate,
    onClose,
    onFilterChange,
    onCancel,
    onCloseClick = onCancel,
    confirmButtonColor = 'primary',
    cancelButtonColor = 'tertiary',
    confirmButtonText = <Trans>Send</Trans>,
    cancelButtonText = <Trans>Cancel</Trans>,
}) => {
    const [showMore, setShowMore] = useState(false);
    const [sending, setSending] = useState(false);
    const [saving, setSaving] = useState(false);
    const [localSetupData, setLocalSetup] = useState<LetterSetupData>();

    useEffect(() => {
        if (letterSetupData) {
            setLocalSetup(letterSetupData);
        }
    }, [letterSetupData]);

    const handleAcceptClick = async () => {
        handleShowMore(false);
        const theList = selectedMeetings.filter((x) => x.isChecked).map((x) => x.meetingId!);
        if (!localSetupData?.sendEmail && !localSetupData?.sendSms) {
            Toast.error(<Trans>You need to select at least one form of reminding</Trans>);
            return;
        }
        setSending(true);
        await sendReminder(theList)
            .then(() => {
                Toast.success(<Trans>Reminders successfully sent</Trans>);
                triggerHandler(onSend);
                triggerHandler(onClose);
            })
            .catch((e) => {
                Toast.error(<Trans>Something went wrong while sending.</Trans>);
            })
            .finally(() => setSending(false));
    };

    const handleCancelClick = () => {
        handleShowMore(false);
        triggerHandler(onCancel);
        triggerHandler(onClose);
    };
    const handleShowMore = (showMore) => {
        setShowMore(showMore);
    };
    const handleRemoveRow = (row: ResidentialUnitMeetingData) => {
        onFilterChange(row, false);
    };
    const handleFilterChange = (unitId) => {
        const oldItem = selectedMeetings.find((x) => x.id === unitId);
        onFilterChange(oldItem!, true);
    };

    const handleChangeValues = (changes: Partial<LetterSetupData>) => {
        const newArray = { ...localSetupData, ...changes };
        setLocalSetup(newArray);
        handleSaveSetup(newArray);
    };

    const handleSaveSetup = async (letterSetup: LetterSetupData) => {
        if (letterSetup && letterSetup.projectId && letterSetup.id) {
            setSaving(true);
            await updateLetterSetupData(letterSetup.projectId, letterSetup.id, letterSetup)
                .then(() => Toast.success(<Trans>Successfully updated setup</Trans>))
                .catch((e) => Toast.error(<Trans>Something went wrong with saving</Trans>))
                .finally(() => {
                    setSaving(false);
                    triggerHandler(onUpdate);
                });
        }
    };

    return (
        <Modal isOpen={show} className={theme.rounded} toggle={onCloseClick}>
            <ModalHeader>
                <FontAwesomeIcon size="2x" icon={faCalendarPlus} />
                <div className={theme.marginTop}>
                    <Trans>Send a reminder for:</Trans> {activeTab.name}
                </div>
            </ModalHeader>
            <ModalBody>
                <div className={theme.marginTop}>
                    <Trans>
                        To ensure a succesfull process, its essensial that the owners are notified well in advance.
                    </Trans>
                </div>
                <div className={classnames(theme.marginTop, theme.reminderContainer)}>
                    {selectedMeetings
                        .filter((x) => x.isChecked)
                        .map((x, index) => (
                            <div key={index} className={theme.unitRow}>
                                {/* Left side */}
                                <div className={theme.unitRowRight}>
                                    <div className={theme.bold}>{x.name}</div>
                                    <div className={theme.reminderGrid}>
                                        <span>
                                            <Trans>Meeting date:</Trans>
                                        </span>
                                        {x.meetingDate ? (
                                            <DateDisplay value={x.meetingDate} monthTextForm={true} />
                                        ) : (
                                            <span>
                                                <Trans>Not set yet</Trans>
                                            </span>
                                        )}
                                        <span>
                                            <Trans>Meeting time:</Trans>
                                        </span>
                                        <span>
                                            {x.timeSlotStart && x.timeSlotEnd ? (
                                                <span>
                                                    {numberToClock(x.timeSlotStart) + ' - ' + numberToClock(x.timeSlotEnd)}
                                                </span>
                                            ) : (
                                                <span>
                                                    <Trans>Not set yet</Trans>
                                                </span>
                                            )}
                                        </span>
                                    </div>
                                </div>
                                {/* Right side */}
                                <Button color="danger" onClick={() => handleRemoveRow(x)}>
                                    <Trans>Remove</Trans>
                                </Button>
                            </div>
                        ))}
                </div>
                <div className={theme.marginTop}>
                    {showMore ? (
                        <div className={theme.addMore}>
                            <div className={theme.addMoreHeader}>
                                <span>
                                    <Trans>Add more units</Trans>
                                </span>
                                <Button color="link" className={theme.miniRounded} onClick={() => handleShowMore(false)}>
                                    <Trans>Cancel</Trans>
                                </Button>
                            </div>
                            <div className={theme.addMoreBody}>
                                <SingleFilterDropDown
                                    label={''}
                                    placeholder={t`Select unit`}
                                    data={selectedMeetings
                                        .filter((x) => !x.isChecked && x.sentDate)
                                        .map((x) => {
                                            let unitInfo =
                                                x.name +
                                                (x.residentialUnitGroupName !== null
                                                    ? ' - ' + x.residentialUnitGroupName
                                                    : '');
                                            const buyerInfo =
                                                x.buyerInfo?.users.length > 0
                                                    ? ' - (' + x.buyerInfo.users.map((u) => u.displayName).join(', ') + ')'
                                                    : '';
                                            return {
                                                id: x.id,
                                                name: unitInfo + buyerInfo,
                                            };
                                        })}
                                    value={''}
                                    onChange={(v) => handleFilterChange(v?.id)}
                                />
                            </div>
                        </div>
                    ) : (
                        <Button color="tertiary" className={theme.miniRounded} onClick={() => handleShowMore(true)}>
                            <FontAwesomeIcon icon={faPlus} /> <Trans>Add more units</Trans>
                        </Button>
                    )}
                    {/* <div className={classnames(theme.marginTop)}>
                        <label>
                            <Trans>Message</Trans>
                        </label>
                        <TextArea
                            value={''}
                            name="message"
                            className="form-control"
                            placeholder={t`Write a message`}
                            onChange={(e) => e.target.value}
                            autoResize
                            minRows={4}
                            maxRows={12}
                        />
                    </div> */}
                </div>
                <div className={classnames(theme.marginTop, theme.notificationContainer)}>
                    <Banner type="info" className={theme.bannerBorder}>
                        <Trans>
                            Changing the sliders below will update those in the setup for{' '}
                            <strong>{localSetupData?.meetingType?.name}</strong>
                        </Trans>
                    </Banner>
                    <div className={theme.notificationBox}>
                        <Trans>SMS</Trans>
                        <SlideToggle
                            onChange={(e) => handleChangeValues({ sendSms: e.target.checked })}
                            checked={localSetupData?.sendSms}></SlideToggle>
                    </div>
                    <div className={theme.notificationBox}>
                        <Trans>Email</Trans>
                        <SlideToggle
                            onChange={(e) => handleChangeValues({ sendEmail: e.target.checked })}
                            checked={localSetupData?.sendEmail}></SlideToggle>
                    </div>
                </div>
            </ModalBody>
            <ModalFooter>
                <Button color={cancelButtonColor} onClick={handleCancelClick} disabled={sending || saving}>
                    {cancelButtonText}
                </Button>
                <Button color={confirmButtonColor} onClick={handleAcceptClick} loading={sending || saving}>
                    {confirmButtonText}
                </Button>
            </ModalFooter>
        </Modal>
    );
};

export default ReminderDialog;
